import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, InfoIcon, MapPinIcon, RulerIcon, TagIcon, UsersIcon } from 'lucide-react';
import { DeliveryOptions } from '../components/DeliveryOptions';
import { getSupabaseErrorMessage, loadProduct } from '../data/repository';
import { Product } from '../data/types';
export function ProductDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageIndex, setImageIndex] = useState(0);
  const [termsOpen, setTermsOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    setError('');
    void loadProduct(id || '').then(setProduct).catch(loadError => setError(getSupabaseErrorMessage(loadError))).finally(() => setIsLoading(false));
  }, [id]);
  if (isLoading) return <div className="min-h-screen bg-background py-24 text-center text-gray-500" data-id="element-696" data-mp="3onsldw72kn7b">
        Učitavamo proizvod…
      </div>;
  if (error) return <div className="min-h-screen bg-background py-24 px-4 text-center" data-id="element-697" data-mp="3atrh6mxcm7zk">
        <p role="alert" className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800" data-id="element-698" data-mp="3fvqmw3gk9gzo">
          {error}
        </p>
      </div>;
  if (!product) return <div className="min-h-screen bg-background py-24 text-center" data-id="element-699" data-mp="bfxszwjhfn0r">
        <h1 className="text-3xl font-bold text-gray-900" data-id="element-700" data-mp="3tlam4zaxnw48">
          Proizvod nije pronađen
        </h1>
        <button type="button" onClick={() => navigate('/')} className="mt-5 rounded-xl bg-primary px-5 py-3 font-bold text-white" data-id="element-701" data-mp="2erajhx9w24v">
          Natrag na početnu
        </button>
      </div>;
  const images = [product.image, ...(product.images || [])];
  const currentImage = images[imageIndex];
  const specs = [{
    icon: RulerIcon,
    label: 'Dimenzije',
    value: product.dimensions
  }, {
    icon: MapPinIcon,
    label: 'Prostor',
    value: product.application
  }, {
    icon: UsersIcon,
    label: 'Nosivost',
    value: product.capacity
  }].filter((item): item is {
    icon: typeof RulerIcon;
    label: string;
    value: string;
  } => Boolean(item.value));
  return <div className="min-h-screen bg-background py-10" data-id="element-702" data-mp="1fdjclxfodv8j">
      <div className="max-w-6xl mx-auto px-4" data-id="element-703" data-mp="1bczjr624xds7">
        <Link to="/ponuda" className="font-bold text-gray-600 hover:text-primary" data-id="element-704" data-mp="1bdsfzd3gwkw3">
          ← Natrag na ponudu
        </Link>
        <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-2" data-id="element-705" data-mp="35qqobr5lx7gs">
          <section data-id="element-706" data-mp="35oxrf3ksq1lz">
            <div className="relative h-80 overflow-hidden rounded-3xl border border-gray-100 bg-gray-100 sm:h-[28rem]" data-id="element-707" data-mp="30rxk6pc7msta">
              {isImageUrl(currentImage) ? <img src={currentImage} alt={product.name} className="h-full w-full object-cover" data-id="element-708" data-mp="38b6jtquwzd55" /> : <span className="flex h-full items-center justify-center text-8xl" data-id="element-709" data-mp="3i1rs660e3w19">
                  {currentImage}
                </span>}
              <span className="absolute right-4 top-4 rounded-full bg-white px-4 py-2 text-sm font-bold text-gray-800" data-id="element-710" data-mp="2pcubg7btu7am">
                {product.category}
              </span>
              {images.length > 1 && <>
                  <button type="button" onClick={() => setImageIndex((imageIndex - 1 + images.length) % images.length)} aria-label="Prethodna slika" className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white p-2 text-gray-800" data-id="element-711" data-mp="2z9yzkcp8gcu2">
                    <ChevronLeftIcon data-id="element-712" data-mp="h6sef6p57w5j" />
                  </button>
                  <button type="button" onClick={() => setImageIndex((imageIndex + 1) % images.length)} aria-label="Sljedeća slika" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white p-2 text-gray-800" data-id="element-713" data-mp="c75y2oe0pzsi">
                    <ChevronRightIcon data-id="element-714" data-mp="v5ibwnfvjxuf" />
                  </button>
                </>}
            </div>
            {images.length > 1 && <div className="mt-4 flex flex-wrap gap-3" data-id="element-715" data-mp="19vpio5wirsfa">
                {images.map((image, index) => <button key={`${image}-${index}`} type="button" onClick={() => setImageIndex(index)} aria-label={`Slika ${index + 1}`} className={`h-16 w-16 overflow-hidden rounded-xl border-2 ${index === imageIndex ? 'border-primary' : 'border-gray-200'}`} data-id="element-716" data-mp="14ww6qhwvs9h8" data-mpi={index}>
                    {isImageUrl(image) ? <img src={image} alt="" className="h-full w-full object-cover" data-id="element-717" data-mp="1ogv0ccpgvmhs" /> : image}
                  </button>)}
              </div>}
          </section>
          <section className="flex flex-col" data-id="element-718" data-mp="1jijsu8mn9o2q">
            <h1 className="text-4xl font-display font-bold text-gray-900" data-id="element-719" data-mp="15qdmtfd7gxh0">
              {product.name}
            </h1>
            {product.description && <section className="mt-5 rounded-2xl border border-gray-100 bg-white p-4 sm:p-5" data-id="element-720" data-mp="19pcqkwn7ao6v">
                <h2 className="text-sm font-bold text-gray-900" data-id="element-721" data-mp="vtr34twm8cgk">Opis</h2>
                <p className="mt-2 whitespace-pre-line break-words text-base leading-7 text-gray-600 sm:text-lg sm:leading-8" data-id="element-722" data-mp="10pfwuglyez4y">
                  {product.description}
                </p>
              </section>}
            <div className="mt-6 space-y-3" data-id="element-723" data-mp="2xn3oqcevxoh2">
              {specs.map(spec => {
              const Icon = spec.icon;
              return <div key={spec.label} className="flex items-center rounded-2xl border border-gray-100 bg-white px-4 py-3" data-id="element-724" data-mp="2opb917594et5">
                    <Icon className="mr-3 text-primary" size={20} data-id="element-725" data-mp="3hdgww2rlqjx7" />
                    <span className="w-28 text-sm text-gray-500" data-id="element-726" data-mp="3hb2ypl1mkrlf">
                      {spec.label}
                    </span>
                    <strong data-id="element-727" data-mp="37f9paaj7de7v">{spec.value}</strong>
                  </div>;
            })}
              {product.ageRestriction && <div className="flex items-center rounded-2xl border border-gray-100 bg-white px-4 py-3" data-id="element-728" data-mp="210ik3un1r781">
                  <InfoIcon className="mr-3 text-primary" size={20} data-id="element-729" data-mp="1vyjhimz4sec5" />
                  <span className="w-28 text-sm text-gray-500" data-id="element-730" data-mp="1lwyyfelrjfv7">Dob</span>
                  <strong data-id="element-731" data-mp="1qur030c7mgcl">{product.ageRestriction}</strong>
                </div>}
            </div>
            {product.variants?.length ? <div className="mt-6" data-id="element-732" data-mp="1vsslvt10strr">
                <h2 className="mb-3 flex items-center gap-2 font-bold text-gray-700" data-id="element-733" data-mp="20kk3he277p5r">
                  <TagIcon size={16} className="text-primary" data-id="element-734" data-mp="24mfexbdufix9" />
                  Opcije
                </h2>
                {product.variants.map(variant => <div key={variant.label} className="mb-2 flex justify-between rounded-2xl border border-gray-100 bg-white px-4 py-3" data-id="element-735" data-mp="29jxg2flmctup">
                    <span data-id="element-736" data-mp="2jj64ksxptg9o">{variant.label}</span>
                    <strong className="text-primary" data-id="element-737" data-mp="k6txg1riqqth">{variant.price} €</strong>
                  </div>)}
              </div> : null}
            <section className="mt-6" data-id="element-738" data-mp="p5x0xhruoozc">
              <button type="button" onClick={() => setTermsOpen(!termsOpen)} aria-expanded={termsOpen} className="flex w-full items-center justify-between rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 font-bold text-gray-900" data-id="element-739" data-mp="2r2xkcbmll9kd">
                <span className="flex items-center gap-2" data-id="element-740" data-mp="2vtnndi7jx0bw">
                  <InfoIcon size={18} className="text-primary" data-id="element-741" data-mp="2vw9wvne05pwr" />
                  Uvjeti korištenja
                </span>
                <ChevronDownIcon className={`text-primary ${termsOpen ? 'rotate-180' : ''}`} data-id="element-742" data-mp="235rs8hxmrgh8" />
              </button>
              {termsOpen && <p className="mt-3 whitespace-pre-line rounded-2xl border border-gray-100 bg-white p-5 text-sm leading-relaxed text-gray-700" data-id="element-743" data-mp="2d3fpbeipl02k">
                  {product.usageTerms || 'Za ovaj proizvod još nisu uneseni posebni uvjeti korištenja.'}
                </p>}
            </section>
            <div className="mt-6 rounded-3xl border border-gray-100 bg-white p-6" data-id="element-744" data-mp="3w1uendgcwtt6">
              <p className="text-sm text-gray-500" data-id="element-745" data-mp="4vjah8g1vcxm">Cijena od</p>
              <p className="text-4xl font-bold text-gray-900" data-id="element-746" data-mp="4tpz5fuhs4ja">
                {product.basePrice} <span className="text-primary" data-id="element-747" data-mp="3m5hb7nv0f1l8">€</span>
              </p>
              <p className="mt-2 text-sm leading-6 text-gray-500" data-id="element-748" data-mp="3r27sufmgq58r">
                Cijena dostave ovisi o lokaciji dostave.
              </p>
              <Link to={`/rezervacija?product=${product.id}`} className="mt-5 block rounded-2xl bg-primary px-5 py-4 text-center text-lg font-bold text-white hover:bg-[#0AC2DC]" data-id="element-749" data-mp="ipaigce4uor8">
                Rezerviraj
              </Link>
            </div>
          </section>
        </div>
        <div className="mt-10" data-id="element-750" data-mp="11m1zef67crs4">
          <DeliveryOptions data-id="element-751" data-mp="11l0ms69jfx8a" />
        </div>
      </div>
    </div>;
}
function isImageUrl(value: string): boolean {
  return value.startsWith('http') || value.startsWith('data:');
}